import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { FeeDTO, CoaAccountDTO } from '@/shared/api/model';
import { 
  useGetAllFeesByProductPropertiesGlNo, 
  useGetGlAccountsForFees, 
  useGetAcAccountsForFees,
  useCreateFee,
  useUpdateFee
} from '@/shared/api/client/fee-resource';

export interface UseFeesProps {
  productGlNo?: string;
}

export interface GlAccount {
  id: string;
  name: string;
}

export const useFees = ({ productGlNo }: UseFeesProps) => {
  const [fees, setFees] = useState<FeeDTO[]>([]);
  const [glAccounts, setGlAccounts] = useState<GlAccount[]>([]);
  const [acAccounts, setAcAccounts] = useState<GlAccount[]>([]);
  
  // Add mutations for bulk operations
  const { mutateAsync: createFeeMutation } = useCreateFee();
  const { mutateAsync: updateFeeMutation } = useUpdateFee();
  
  // Fetch fees for the product
  const { 
    data: productFees, 
    isLoading: isLoadingFees, 
    refetch: refetchFees 
  } = useGetAllFeesByProductPropertiesGlNo(productGlNo || '', {
    query: {
      enabled: !!productGlNo
    }
  });
  
  // Fetch GL accounts
  const { 
    data: glAccountsData, 
    isLoading: isLoadingGlAccounts 
  } = useGetGlAccountsForFees();
  
  // Fetch AC accounts
  const { 
    data: acAccountsData, 
    isLoading: isLoadingAcAccounts 
  } = useGetAcAccountsForFees();

  // Update fees when product fees are loaded
  useEffect(() => {
    if (productFees) {
      setFees(productFees);
    } else {
      setFees([]);
    }
  }, [productFees]);

  // Update GL accounts when data is loaded
  useEffect(() => {
    if (glAccountsData) {
      const formattedAccounts = glAccountsData.map((acc: CoaAccountDTO) => ({
        id: acc.accountId || '',
        name: acc.acName || ''
      }));
      setGlAccounts(formattedAccounts);
    }
  }, [glAccountsData]);

  // Update AC accounts when data is loaded
  useEffect(() => {
    if (acAccountsData) {
      const formattedAccounts = acAccountsData.map((acc: CoaAccountDTO) => ({
        id: acc.accountId || '',
        name: acc.acName || ''
      }));
      setAcAccounts(formattedAccounts);
    } else {
      setAcAccounts([]);
    }
  }, [acAccountsData]);

  // Handle fees change
  const handleFeesChange = useCallback((updatedFees: FeeDTO[]) => {
    setFees(updatedFees);
  }, []);

  // Prepare fees for saving
  const prepareFeesForSave = useCallback((): FeeDTO[] => {
    return fees.map(fee => ({
      ...fee,
      productPropertiesGlNo: productGlNo
    }));
  }, [fees, productGlNo]);

  // Save all fees to the backend
  const saveAllFees = useCallback(async () => {
    if (!productGlNo) {
      console.error('Cannot save fees: productGlNo is missing');
      return [];
    }

    try {
      const preparedFees = prepareFeesForSave();
      const savedFees: FeeDTO[] = [];

      // Process each fee
      for (const fee of preparedFees) {
        if (fee.id) {
          // Update existing fee
          const response = await updateFeeMutation({ id: fee.id, data: fee });
          savedFees.push(response);
        } else {
          // Create new fee
          const response = await createFeeMutation({ data: fee });
          savedFees.push(response);
        }
      }

      setFees(savedFees);
      return savedFees;
    } catch (error) {
      console.error('Error saving fees:', error);
      message.error('Failed to save some fees');
      return fees;
    }
  }, [fees, productGlNo, prepareFeesForSave, createFeeMutation, updateFeeMutation]);

  return {
    fees,
    glAccounts,
    acAccounts,
    isLoading: isLoadingFees || isLoadingGlAccounts || isLoadingAcAccounts,
    handleFeesChange,
    prepareFeesForSave,
    saveAllFees,
    refetchFees
  };
};

export default useFees;
